<template>
    <div class="w-full flex flex-col gap-3">
        <span v-if="isLoadingLocations" class="text-theme-muted">
            Cargando direcciones del cliente...
        </span>

        <div v-else class="flex flex-col gap-2">
            <custom-radio
                v-for="item in locations"
                :key="item.id"
                v-model="selectedOption"
                :val="item.id"
                @input="onSelectOption"
            >
                <span>
                    {{ item.address | address }}
                    {{ item.addressComplement }}
                    <span class="text-theme-muted">
                        · {{ item.city || item.cityCode }}
                    </span>
                </span>
            </custom-radio>
            <custom-radio
                v-model="selectedOption"
                :val="$data.$newLocationOption"
                @input="onSelectOption"
            >
                <span>Nueva dirección</span>
            </custom-radio>
        </div>

        <div
            v-if="selectedOption === $data.$newLocationOption"
            class="grid grid-cols-1 md:grid-cols-2 gap-3"
        >
            <custom-input
                v-model="newLocation.address"
                label="Dirección"
                :error="
                    introValidators.address && !isValidAddress
                        ? 'Dirección no es valida'
                        : ''
                "
                @blur="onBlurField('address')"
            />
            <city-code-selector
                v-model="newLocation.cityCode"
                enabled
                @change="emitNewLocation"
            />
            <custom-input
                v-model="newLocation.addressComplement"
                label="Complemento de dirección"
                @blur="emitNewLocation"
            />
            <custom-input
                v-model="newLocation.neighborhood"
                label="Barrio"
                @blur="emitNewLocation"
            />
            <custom-input
                v-model="newLocation.receiverName"
                label="Nombre de quien recibe"
                :error="
                    introValidators.receiverName && !isValidReceiverName
                        ? 'Nombre no es valido'
                        : ''
                "
                @blur="onBlurField('receiverName')"
            />
            <custom-input
                v-model="newLocation.receiverPhone"
                label="Celular de quien recibe"
                :error="
                    introValidators.receiverPhone && !isValidReceiverPhone
                        ? 'Celular no es valido'
                        : ''
                "
                @blur="onBlurField('receiverPhone')"
            />
        </div>
    </div>
</template>

<script>
import { mapActions } from "vuex";

import { ENABLED_CITIES } from "@/common/constants";
import { addressRegex, nameRegex, phoneRegex } from "@/common/lib/regex";

import CityCodeSelector from "@/components/utils/CityCodeSelector.vue";
import CustomInput from "@/components/utils/CustomInput.vue";

const NEW_LOCATION_OPTION = "new";

export default {
    name: "DeliveryLocationSelector",
    components: { CityCodeSelector, CustomInput },
    props: {
        customerId: {
            type: String,
            required: true
        },
        // { customerLocationId, cityCode } | { location, cityCode } | null
        value: {
            type: Object,
            default: () => null
        }
    },
    emits: ["input"],
    data: () => ({
        locations: [],
        isLoadingLocations: false,
        selectedOption: null,
        newLocation: {
            address: "",
            addressComplement: "",
            neighborhood: "",
            cityCode: "11001",
            receiverName: "",
            receiverPhone: ""
        },
        introValidators: {
            address: false,
            receiverName: false,
            receiverPhone: false
        },
        $newLocationOption: NEW_LOCATION_OPTION
    }),
    computed: {
        isValidAddress() {
            return addressRegex.test(this.newLocation.address);
        },
        isValidReceiverName() {
            return nameRegex.test(this.newLocation.receiverName);
        },
        isValidReceiverPhone() {
            return phoneRegex.test(this.newLocation.receiverPhone);
        },
        isValidNewLocation() {
            return (
                this.isValidAddress &&
                this.isValidReceiverName &&
                this.isValidReceiverPhone &&
                !!this.newLocation.cityCode
            );
        }
    },
    watch: {
        customerId() {
            this.loadLocations();
        }
    },
    mounted() {
        if (this.value && this.value.customerLocationId) {
            this.selectedOption = this.value.customerLocationId;
        }
        if (this.value && this.value.location) {
            this.selectedOption = NEW_LOCATION_OPTION;
            this.newLocation = { ...this.newLocation, ...this.value.location };
        }
        this.loadLocations();
    },
    methods: {
        ...mapActions("customers", ["listCustomerLocations"]),
        async loadLocations() {
            if (!this.customerId) return;
            this.isLoadingLocations = true;
            try {
                this.locations = await this.listCustomerLocations({
                    customerId: this.customerId,
                    complete: true,
                    page: 1
                });
                if (!this.locations.length && !this.selectedOption) {
                    this.selectedOption = NEW_LOCATION_OPTION;
                }
                this.isLoadingLocations = false;
            } catch (error) {
                this.isLoadingLocations = false;
                this.locations = [];
                this.selectedOption = NEW_LOCATION_OPTION;
            }
        },
        onSelectOption(option) {
            this.selectedOption = option;
            if (option === NEW_LOCATION_OPTION) {
                this.emitNewLocation();
                return;
            }
            const location = this.locations.find((item) => item.id === option);
            this.$emit("input", {
                customerLocationId: location.id,
                cityCode: location.cityCode
            });
        },
        onBlurField(field) {
            this.introValidators[field] = true;
            this.emitNewLocation();
        },
        emitNewLocation() {
            if (!this.isValidNewLocation) {
                this.$emit("input", null);
                return;
            }
            const { cityCode } = this.newLocation;
            this.$emit("input", {
                cityCode,
                location: {
                    ...this.newLocation,
                    cityName: ENABLED_CITIES[cityCode]
                }
            });
        }
    }
};
</script>
